import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Clock,
  Coins,
  Compass,
  Hotel,
  Loader2,
  MapPin,
  Moon,
  Plus,
  Sparkles,
  Train,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";
import { api, formatApiError, fmtDuration } from "../lib/api";
import CityAutocomplete from "../components/CityAutocomplete";
import Footer from "../components/Footer";
import { whatsappUrl } from "../lib/whatsapp";
import { bookingCityUrl, trainlineSearchUrl, omioSearchUrl, interrailPassUrl, trackAffiliateClick } from "../lib/affiliates";

/* Helpers */
const todayISO = () => new Date().toISOString().slice(0, 10);
const inSixWeeksISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 42);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

/* Build a city autocomplete row with remove button */
function StopRow({ value, onChange, onRemove, label, removable }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
      <div>
        <label className="iq-label">{label}</label>
        <CityAutocomplete value={value} onChange={onChange} />
      </div>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          data-testid={`plan-trip-remove-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className="iq-btn-ghost h-[42px]"
          aria-label="Remove stop"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function PlanTrip() {
  const [mode, setMode] = useState("auto"); // "auto" | "manual"
  const [fromCity, setFromCity] = useState(null);
  const [toCity, setToCity] = useState(null);
  const [stops, setStops] = useState([]); // intermediate cities (manual mode only)
  const [totalDays, setTotalDays] = useState(7);
  const [startDate, setStartDate] = useState(inSixWeeksISO());
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");

  const addStop = () => {
    if (stops.length >= 5) return;
    setStops([...stops, null]);
  };
  const updateStop = (i, v) => setStops(stops.map((s, idx) => (idx === i ? v : s)));
  const removeStop = (i) => setStops(stops.filter((_, idx) => idx !== i));

  const minStops = 0;
  const maxStops = 5;
  const canSubmit =
    fromCity?.id && toCity?.id && totalDays >= 2 && totalDays <= 30 && startDate &&
    stops.every((s) => !s || s.id);

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) {
      setErr("Pick both From and To, and fill in any intermediate stops.");
      return;
    }
    setErr("");
    setLoading(true);
    setPlan(null);
    try {
      const endpoint = mode === "auto" ? "/auto-route" : "/plan-trip";
      const payload = {
        from: { id: fromCity.id, name: fromCity.name },
        to: { id: toCity.id, name: toCity.name },
        total_days: Number(totalDays),
        start_date: startDate,
      };
      if (mode === "manual") {
        payload.stops = stops.filter((s) => s && s.id).map((s) => ({ id: s.id, name: s.name }));
      }
      const { data } = await api.post(endpoint, payload);
      setPlan(data);
      requestAnimationFrame(() => {
        document.getElementById("plan-trip-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  /* Compute arrival dates per city for the timeline */
  const arrivalDateForStop = (i) => {
    if (!plan) return null;
    let cursor = new Date(plan.legs[0]?.depart_search_time || startDate);
    cursor = new Date(startDate);
    let day = 0;
    for (let k = 0; k < i; k++) day += plan.stops[k].nights;
    const arr = new Date(cursor);
    arr.setDate(arr.getDate() + day);
    return arr.toISOString().slice(0, 10);
  };

  return (
    <div data-testid="plan-trip-page" className="pt-safe-navbar pb-20 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-5 md:px-6">
        {/* Header */}
        <div className="mb-8 pt-2">
          <div className="iq-overline mb-2 inline-flex items-center gap-2">
            <Compass className="h-3 w-3" /> Multi-stop interrail planner
          </div>
          <h1 className="font-display text-cream text-4xl md:text-5xl tracking-wider mb-3 leading-none">
            Plan your <span className="text-gold">whole</span> journey.
          </h1>
          <p className="font-serif italic text-cream-dim text-lg max-w-2xl leading-relaxed">
            Pick where you start, where you end, and any cities you'd like to stop in. We'll fetch real
            train times for every leg and suggest how many nights to spend in each.
          </p>
        </div>

        {/* Mode toggle */}
        <div data-testid="plan-trip-mode-toggle" className="iq-card p-1.5 mb-5 grid grid-cols-2 gap-1">
          <button
            type="button"
            data-testid="plan-trip-mode-auto"
            onClick={() => { setMode("auto"); setStops([]); setPlan(null); }}
            className={`px-3 py-3 rounded-sm font-display tracking-[0.15em] text-xs md:text-sm uppercase transition-colors ${
              mode === "auto" ? "bg-gold text-ink" : "text-cream-dim hover:text-cream"
            }`}
          >
            <Sparkles className="inline h-3 w-3 mr-1.5" /> Auto-plan everything
          </button>
          <button
            type="button"
            data-testid="plan-trip-mode-manual"
            onClick={() => { setMode("manual"); setPlan(null); }}
            className={`px-3 py-3 rounded-sm font-display tracking-[0.15em] text-xs md:text-sm uppercase transition-colors ${
              mode === "manual" ? "bg-gold text-ink" : "text-cream-dim hover:text-cream"
            }`}
          >
            <MapPin className="inline h-3 w-3 mr-1.5" /> Pick stops myself
          </button>
        </div>
        <p className="text-cream-dim/70 text-xs font-serif italic mb-4 -mt-2">
          {mode === "auto"
            ? "We choose the optimal cities along the way, allocate nights, and estimate train + hostel costs. Sleeper-train days are flagged."
            : "Add the cities you want to visit. We'll calculate trains and split nights across each."}
        </p>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="plan-trip-form"
          className="iq-card p-5 md:p-7 space-y-5 mb-8"
        >
          <StopRow
            value={fromCity}
            onChange={setFromCity}
            label="Departing from"
            removable={false}
          />

          {mode === "manual" && stops.map((s, i) => (
            <StopRow
              key={i}
              value={s}
              onChange={(v) => updateStop(i, v)}
              onRemove={() => removeStop(i)}
              label={`Stop ${i + 1}`}
              removable
            />
          ))}

          {mode === "manual" && stops.length < maxStops && (
            <button
              type="button"
              data-testid="plan-trip-add-stop"
              onClick={addStop}
              className="iq-btn-ghost text-sm border border-dashed border-panel-border hover:border-gold/40 hover:text-gold w-full justify-center py-3"
            >
              <Plus className="h-4 w-4" />
              {stops.length === 0 ? "Add a city in between" : "Add another stop"}
            </button>
          )}

          <StopRow
            value={toCity}
            onChange={setToCity}
            label="Ending at"
            removable={false}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="iq-label">Total days</label>
              <input
                data-testid="plan-trip-days"
                type="number"
                min="2"
                max="30"
                value={totalDays}
                onChange={(e) => setTotalDays(e.target.value)}
                className="iq-input"
              />
            </div>
            <div>
              <label className="iq-label">Start date</label>
              <input
                data-testid="plan-trip-start"
                type="date"
                value={startDate}
                min={todayISO()}
                onChange={(e) => setStartDate(e.target.value)}
                className="iq-input"
              />
            </div>
          </div>

          {err && (
            <div data-testid="plan-trip-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm">
              {err}
            </div>
          )}

          <button
            type="submit"
            data-testid="plan-trip-submit"
            disabled={!canSubmit || loading}
            className="iq-btn-primary w-full justify-center disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Fetching live timetables…</>
            ) : (
              <><Sparkles className="h-5 w-5" /> Plan my journey <ArrowRight className="h-5 w-5" /></>
            )}
          </button>
          <p className="text-[11px] text-cream-dim/60 italic text-center">
            Live timetables via Transitous. Prices are indicative estimates.
          </p>
        </form>

        {/* Results */}
        {plan && (
          <div id="plan-trip-result" data-testid="plan-trip-result" className="space-y-6">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="iq-card p-4 text-center">
                <Moon className="h-5 w-5 text-gold mx-auto mb-1" />
                <div className="font-display text-cream text-2xl">{plan.total_nights_allocated}</div>
                <div className="text-cream-dim text-[10px] uppercase tracking-widest">nights</div>
              </div>
              <div className="iq-card p-4 text-center">
                <Clock className="h-5 w-5 text-gold mx-auto mb-1" />
                <div className="font-display text-cream text-2xl">{Math.round(plan.total_duration_seconds / 3600)}h</div>
                <div className="text-cream-dim text-[10px] uppercase tracking-widest">on the rails</div>
              </div>
              <div className="iq-card p-4 text-center">
                <Train className="h-5 w-5 text-gold mx-auto mb-1" />
                <div className="font-display text-gold text-xl">€{plan.total_rail_eur ?? plan.total_est_price_eur}</div>
                <div className="text-cream-dim text-[10px] uppercase tracking-widest">rail</div>
              </div>
              {plan.total_accommodation_eur != null ? (
                <div className="iq-card p-4 text-center">
                  <Hotel className="h-5 w-5 text-gold mx-auto mb-1" />
                  <div className="font-display text-gold text-xl">€{plan.total_accommodation_eur}</div>
                  <div className="text-cream-dim text-[10px] uppercase tracking-widest">stays</div>
                </div>
              ) : (
                <div className="iq-card p-4 text-center">
                  <Train className="h-5 w-5 text-gold mx-auto mb-1" />
                  <div className="font-display text-cream text-2xl">{plan.legs_resolved}/{plan.legs.length}</div>
                  <div className="text-cream-dim text-[10px] uppercase tracking-widest">legs resolved</div>
                </div>
              )}
            </div>

            {/* Auto-mode grand-total banner */}
            {plan.mode === "auto" && (
              <div className="iq-card p-5 md:p-6 bg-gradient-to-br from-gold/10 via-panel to-ink border-gold/30">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="iq-overline text-gold/80 mb-1">
                      <Sparkles className="inline h-3 w-3 mr-1" /> Auto-plan estimate · {plan.n_intermediate_stops_picked} cities along the way
                    </div>
                    <p className="text-cream-dim text-sm font-serif italic max-w-xl">
                      Rail + bed for the whole trip. Hostel-tier estimate based on median dorm prices in each city. Pay only for the rail tickets you book through us; stays are optional via the Booking.com links below.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-gold text-4xl tracking-tight leading-none">€{plan.total_est_price_eur}</div>
                    <div className="text-cream-dim text-xs mt-1">€{plan.total_rail_eur} rail + €{plan.total_accommodation_eur} stays</div>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="iq-card p-5 md:p-7">
              <div className="iq-overline mb-4 inline-flex items-center gap-2">
                <MapPin className="h-3 w-3" /> Your journey · {fmtDate(startDate)} onward
              </div>

              <div className="relative pl-2 md:pl-4">
                {/* track line */}
                <div className="absolute left-[10px] md:left-[14px] top-3 bottom-3 w-px bg-gradient-to-b from-gold/60 via-gold/40 to-gold/60" />

                {plan.stops.map((s, i) => {
                  const arrival = arrivalDateForStop(i);
                  const departDate = arrival
                    ? new Date(new Date(arrival).getTime() + s.nights * 86400000).toISOString().slice(0, 10)
                    : null;
                  const leg = plan.legs[i]; // outgoing leg from this city
                  return (
                    <React.Fragment key={`${s.id}-${i}`}>
                      {/* City stop card */}
                      <div className="relative pl-7 md:pl-10 pb-4" data-testid={`plan-trip-stop-${i}`}>
                        <span className="absolute left-[2px] md:left-[6px] top-3 h-4 w-4 rounded-full bg-gold ring-4 ring-ink animate-pulse-gold" />
                        <div className="iq-card p-4 hover:border-gold/40 transition-colors">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="iq-overline text-gold/80 mb-1">{i === 0 ? "Start" : i === plan.stops.length - 1 ? "End" : `Stop ${i}`}</div>
                              <h3 className="font-display text-cream text-xl md:text-2xl tracking-wider">{s.name}</h3>
                              <p className="text-cream-dim text-xs mt-1">
                                <Calendar className="inline h-3 w-3 mr-1 text-gold" />
                                {fmtDate(arrival)}
                                {departDate && departDate !== arrival && <> → {fmtDate(departDate)}</>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="inline-flex items-center gap-1 bg-gold/15 border border-gold/40 text-gold rounded-sm px-2 py-1 text-xs font-display tracking-widest uppercase">
                                <Moon className="h-3 w-3" /> {s.nights} {s.nights === 1 ? "night" : "nights"}
                              </span>
                              {s.accommodation_eur != null && (
                                <span className="inline-flex items-center gap-1 bg-ink border border-panel-border text-cream-dim rounded-sm px-2 py-1 text-xs">
                                  <Hotel className="h-3 w-3 text-gold" /> ~€{s.accommodation_eur}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                            <a
                              href={bookingCityUrl(s.name)}
                              target="_blank"
                              rel="sponsored noopener noreferrer"
                              onClick={() => trackAffiliateClick("booking", { city: s.name, source: "plan_trip_stop" })}
                              data-testid={`plan-trip-stay-${i}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-ink border border-panel-border text-cream-dim hover:text-gold hover:border-gold/40 transition-colors"
                            >
                              <Hotel className="h-3 w-3" /> Stays in {s.name}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Rail leg between this city and the next */}
                      {leg && (
                        <div className="relative pl-7 md:pl-10 pb-4">
                          <span className="absolute left-[6px] md:left-[10px] top-2 h-3 w-3 rounded-full border-2 border-gold/60 bg-ink" />
                          <div className="border border-dashed border-gold/30 bg-gold/[0.03] rounded-sm p-3 md:p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0">
                                <div className="iq-overline text-cream-dim/70 mb-1">
                                  <Train className="inline h-3 w-3 mr-1 text-gold" /> Travel day {(plan.stops.slice(0, i + 1).reduce((a, b) => a + b.nights, 0)) + 1}
                                </div>
                                <p className="text-cream text-sm font-display tracking-wider">
                                  {leg.from_city} <ArrowRight className="inline h-4 w-4 text-gold mx-1" /> {leg.to_city}
                                </p>
                                {leg.available ? (
                                  <p className="text-cream-dim text-xs mt-1.5">
                                    <Clock className="inline h-3 w-3 mr-1 text-gold" />
                                    {fmtDuration(leg.duration_seconds)} · {leg.transfers} {leg.transfers === 1 ? "transfer" : "transfers"}
                                  </p>
                                ) : (
                                  <p className="text-amber-300/90 text-xs mt-1.5 italic">No direct route resolved — chat to our conductor for a custom itinerary.</p>
                                )}
                                {leg.sleeper_recommended && (
                                  <p className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm bg-indigo-500/15 border border-indigo-400/40 text-indigo-200">
                                    <Moon className="h-3 w-3" /> Sleeper train recommended · saves a hostel night
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {leg.est_price_eur != null && (
                                  <span className="font-display text-gold text-lg">€{leg.est_price_eur}</span>
                                )}
                                <a
                                  href={trainlineSearchUrl({ from: leg.from_city, to: leg.to_city, dateISO: leg.depart_search_time })}
                                  target="_blank"
                                  rel="sponsored noopener noreferrer"
                                  onClick={() => trackAffiliateClick("trainline", { from: leg.from_city, to: leg.to_city, source: "plan_trip_leg" })}
                                  data-testid={`plan-trip-leg-trainline-${i}`}
                                  className="iq-btn-ghost text-xs"
                                >
                                  Find on Trainline <ArrowRight className="h-3 w-3" />
                                </a>
                                <a
                                  href={omioSearchUrl(leg.from_city, leg.to_city, leg.depart_search_time)}
                                  target="_blank"
                                  rel="sponsored noopener noreferrer"
                                  onClick={() => trackAffiliateClick("omio", { from: leg.from_city, to: leg.to_city, source: "plan_trip_leg_fallback" })}
                                  data-testid={`plan-trip-leg-omio-${i}`}
                                  className="text-cream-dim/70 hover:text-cream text-[11px] underline underline-offset-2 decoration-dotted"
                                  title="Backup search — useful when Trainline doesn't cover this route"
                                >
                                  or Omio
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Footer CTAs */}
              <div className="mt-6 border-t border-panel-border pt-5 space-y-5">
                {/* Pass upsell — only meaningful with 3+ legs. Most lucrative click in the app. */}
                {plan.legs.length >= 3 && (
                  <a
                    href={interrailPassUrl("global")}
                    target="_blank"
                    rel="sponsored noopener noreferrer"
                    onClick={() => trackAffiliateClick("interrail", { legs: plan.legs.length, source: "plan_trip_pass_upsell" })}
                    data-testid="plan-trip-pass-cta"
                    className="block bg-gradient-to-r from-gold/15 via-gold/5 to-transparent border border-gold/40 rounded-sm p-4 hover:border-gold/70 transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="iq-overline text-gold">An Interrail Pass usually beats single tickets at this length</div>
                        <p className="text-cream text-sm mt-1">
                          {plan.legs.length} legs · ~€{plan.total_est_price_eur} in singles. A Global Pass often comes in cheaper and lets you change plans on the day.
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gold opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
                    </div>
                  </a>
                )}

                <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
                  <p className="text-cream-dim text-xs font-serif italic max-w-md">
                    Prices include rail only. Add accommodation at each stop via the "Stays in…" links — InterQuest earns a small affiliate fee at no cost to you.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <a
                      href={whatsappUrl({ custom: `Hi InterQuest — I'd like to book this trip: ${plan.stops.map((s) => s.name).join(" → ")} (${plan.total_days} days, starting ${startDate}). Estimated rail €${plan.total_est_price_eur}.` })}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="plan-trip-whatsapp"
                      className="iq-btn-primary text-sm"
                    >
                      Book this with a conductor
                    </a>
                    <Link to="/groups" data-testid="plan-trip-cta-groups" className="iq-btn-ghost text-sm">
                      Group quote
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Helpful hint when empty */}
        {!plan && (
          <div className="iq-card p-6 md:p-8 mt-4 flex items-start gap-4 bg-gradient-to-br from-panel via-ink to-navy">
            <TrendingUp className="h-6 w-6 text-gold mt-1 shrink-0" />
            <div>
              <h4 className="font-display text-cream text-xl tracking-wider mb-1">Try this</h4>
              <p className="text-cream-dim text-sm font-serif italic leading-relaxed">
                Rome → Vienna → Copenhagen across 5 days. Two nights in the Eternal City,
                a night swirling through Vienna, two nights between the canals of København.
                Hit "Plan my journey" and see live train options for every leg in seconds.
              </p>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
